import React from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import {
  CLIENT_SELF_PAY_STATUS,
  SELF_PAY_ZENDESK_ARTICLE_URL,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { openUrl } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/expo-linking'

const InlineText = styled(BodyText)`
  display: contents;
`

const linkFormatter = (color: string) => (chunks: string) => {
  return (
    <Link
      onPress={() => openUrl(SELF_PAY_ZENDESK_ARTICLE_URL)}
      size={BodyTextSize.DEFAULT}
      style={{ textDecorationLine: 'underline', color }}
      text={chunks}
    />
  )
}

const successFormatter = (colors: { textSuccess: string }) => (chunks: string) => (
  <InlineText bold color={colors.textSuccess} size={BodyTextSize.DEFAULT} text={chunks} />
)

const warningFormatter = (colors: { textWarning: string }) => (chunks: string) => (
  <InlineText bold color={colors.textWarning} size={BodyTextSize.DEFAULT} text={chunks} />
)

export const useSelfPayContinuationOfCareStatus = (
  clientDetails: ClientObject | undefined,
  programName: string | undefined,
  sessionsRemaining: number,
) => {
  const { isSelfPayEnabled } = useFlags()
  const { formatMessage } = useIntl()
  const { colors } = useTheme()

  const clientFirstName = clientDetails?.preferred_first_name || clientDetails?.first_name || ''
  const customerName = clientDetails?.displayed_employer ?? '-'
  const customerSupportsSelfPay = clientDetails?.customer_support_self_pay
  const selfPayStatus = clientDetails?.self_pay_status

  const underlinedLink = linkFormatter(colors.textSecondary)
  const success = successFormatter(colors)
  const warning = warningFormatter(colors)

  if (!isSelfPayEnabled || !customerSupportsSelfPay || !Programs.getProgramConfigByName(programName)?.selfPayEligible) {
    return null
  } else if (sessionsRemaining <= 1 && selfPayStatus === CLIENT_SELF_PAY_STATUS.ACTIVE) {
    return formatMessage(
      {
        defaultMessage:
          '{clientFirstName} <success>is eligible for self-pay</success>. {customerName} offers a self-pay option for clients to pay for sessions themselves. <link>Learn more about self-pay here.</link>',
        description: 'Text informing that the client is out of covered sessions and they can use self-pay',
      },
      { clientFirstName, customerName, link: underlinedLink, success },
    )
  } else if (sessionsRemaining === 0 && selfPayStatus === CLIENT_SELF_PAY_STATUS.ELIGIBLE) {
    return formatMessage(
      {
        defaultMessage:
          '{clientFirstName} has <warning>run out of covered sessions</warning>. {customerName} offers a self-pay option for clients to pay for sessions themselves. If you and {clientFirstName} agree to continue, <link>you can learn more about self-pay here.</link>',
        description:
          'Text informing that the client is out of covered sessions and they can use self-pay when it is set up',
      },
      { clientFirstName, customerName, link: underlinedLink, warning },
    )
  } else {
    return null
  }
}
